import { createSlice } from "@reduxjs/toolkit";
import {
  setNodesToState,
  setEdgesToState,
  setNodeIdToState,
  setSelectedNodeInfoToState,
  setSelectedEdgeInfoToState,
  setOpenRightPanelToState,
  setFirstNodeInfoToState,
  setInitialNodeTextToState,
  setDisabledWFToState,
  setIsTemplateToState,
} from "./designerSlice";

const initialRightPanelForm = {
  selectedOption: {},
  delayUnit: "",
  responseType: "",
  selectedResponse: [],
  responseWait: false,
  delay: "",
  smsTemp: null,
  emailTemp: null,
  httpRequest: {
    headers: [],
    methodType: "post",
    webHookURL: "",
    requestType: 1,
    body: '{"id":"#lead.id","firstName":"#lead.firstName","lastName":"#lead.lastName","email":"#lead.email","phone":"#lead.phone"}',
  },
  taskTemp: null,
  tempCondition: null,
  apptTemp: null,
  leadAttribute: null,
};

const initialTemplateDraft = {
  hasUnsavedChanges: false,
  mode: null,
  templateId: null,
  name: "",
  nodeId: "1",
  nodes: [],
  edges: [],
  firstNodeInfo: null,
  selectedNodeInfo: null,
  selectedEdgeInfo: null,
  openRightPanel: false,
  disabledWF: false,
  rightPanelForm: initialRightPanelForm,
};

const initialSnapshotState = {
  hasUnsavedChanges: false,
  mode: null,
  workflowId: null,
  // Designer canvas state
  nodeId: "1",
  nodes: [],
  edges: [],
  firstNodeInfo: null,
  selectedNodeInfo: null,
  selectedEdgeInfo: null,
  openRightPanel: false,
  initialNodeText: "",
  disabledWF: false,
  isTemplate: false,
  // Wizard state
  currentStep: 1,
  stepsComplete: false,
  stepsData: {
    step1: {},
    step2: {},
    step3: {
      optOutEducatingTextEnabled: false,
      optOutEducatingText:
        "Reply STOP[WFCode] to unsubscribe from this campaign, or reply STOP ALL to opt out of all future messages from us.",
    },
  },
  // Workflow metadata
  workflowName: "",
  WFType: "",
  selectedTrigger: null,
  hideAddNode: true,
  rightPanelForm: initialRightPanelForm,
  triggerDraft: { categoryId: "", triggerId: "" },
  // Viewport (if available)
  viewport: { x: 0, y: 0, zoom: 1 },
  // Template editor draft. Kept separate so template navigation
  // cannot overwrite an in-progress automation snapshot.
  templateDraft: initialTemplateDraft,
};

export const workflowSnapshotSlice = createSlice({
  name: "workflowSnapshot",
  initialState: initialSnapshotState,
  reducers: {
    saveWorkflowSnapshot: (state, action) => {
      const snapshot = action.payload;
      // Merge the new snapshot with existing state
      Object.assign(state, snapshot, { hasUnsavedChanges: true });
    },

    clearWorkflowSnapshot: (state) => {
      const templateDraft = state.templateDraft;
      return { ...initialSnapshotState, templateDraft };
    },

    restoreWorkflowSnapshot: (state) => {
      // Mark as being restored (cleared after restoration completes)
      state.isRestoring = true;
    },

    markSnapshotRestored: (state) => {
      state.isRestoring = false;
    },

    // Wizard state actions - continuous save
    setWorkflowName: (state, action) => {
      state.workflowName = action.payload;
      state.hasUnsavedChanges = true;
    },

    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
      state.hasUnsavedChanges = true;
    },

    setStepsComplete: (state, action) => {
      state.stepsComplete = action.payload;
      state.hasUnsavedChanges = true;
    },

    setStepsData: (state, action) => {
      state.stepsData = action.payload;
      state.hasUnsavedChanges = true;
    },

    updateStepData: (state, action) => {
      const { step, data } = action.payload;
      state.stepsData[step] = data;
      state.hasUnsavedChanges = true;
    },

    setWFType: (state, action) => {
      state.WFType = action.payload;
      state.hasUnsavedChanges = true;
    },

    setSelectedTrigger: (state, action) => {
      state.selectedTrigger = action.payload;
      state.hasUnsavedChanges = true;
    },

    setHideAddNode: (state, action) => {
      state.hideAddNode = action.payload;
      state.hasUnsavedChanges = true;
    },

    // Right panel form state actions - continuous save
    setRightPanelFormField: (state, action) => {
      const { field, value } = action.payload;
      if (state.isTemplate) {
        state.templateDraft.rightPanelForm[field] = value;
        state.templateDraft.hasUnsavedChanges = true;
        return;
      }
      state.rightPanelForm[field] = value;
      state.hasUnsavedChanges = true;
    },

    resetRightPanelForm: (state) => {
      if (state.isTemplate) {
        state.templateDraft.rightPanelForm = initialRightPanelForm;
        state.templateDraft.hasUnsavedChanges = true;
        return;
      }
      state.rightPanelForm = initialRightPanelForm;
      state.hasUnsavedChanges = true;
    },

    setTemplateDraftMeta: (state, action) => {
      const { mode, templateId } = action.payload || {};
      state.templateDraft.mode = mode ?? null;
      state.templateDraft.templateId = templateId ?? null;
    },

    setTemplateDraftName: (state, action) => {
      state.templateDraft.name = action.payload || "";
      state.templateDraft.hasUnsavedChanges = true;
    },

    clearTemplateDraft: (state) => {
      state.templateDraft = {
        hasUnsavedChanges: false,
        mode: null,
        templateId: null,
        name: "",
        nodeId: "1",
        nodes: [],
        edges: [],
        firstNodeInfo: null,
        selectedNodeInfo: null,
        selectedEdgeInfo: null,
        openRightPanel: false,
        disabledWF: false,
        rightPanelForm: {
          ...initialRightPanelForm,
          httpRequest: { ...initialRightPanelForm.httpRequest, headers: [] },
          selectedResponse: [],
        },
      };
    },

    // Trigger draft actions - continuous save
    setTriggerDraft: (state, action) => {
      state.triggerDraft = action.payload;
      state.hasUnsavedChanges = true;
    },

    resetTriggerDraft: (state) => {
      state.triggerDraft = { categoryId: "", triggerId: "" };
      state.hasUnsavedChanges = true;
    },

    // Mode and workflowId setters (typically set on init)
    setSnapshotMode: (state, action) => {
      state.mode = action.payload;
    },

    setSnapshotWorkflowId: (state, action) => {
      state.workflowId = action.payload;
    },
  },

  // Sync designer slice changes into snapshot (continuous save for designer state)
  extraReducers: (builder) => {
    const writeDesigner = (state, key, value) => {
      if (state.isTemplate) {
        state.templateDraft[key] = value;
        state.templateDraft.hasUnsavedChanges = true;
        return;
      }
      state[key] = value;
      state.hasUnsavedChanges = true;
    };

    builder
      .addCase(setNodesToState, (state, action) => {
        writeDesigner(state, "nodes", action.payload);
      })
      .addCase(setEdgesToState, (state, action) => {
        writeDesigner(state, "edges", action.payload);
      })
      .addCase(setNodeIdToState, (state, action) => {
        writeDesigner(state, "nodeId", action.payload);
      })
      .addCase(setSelectedNodeInfoToState, (state, action) => {
        writeDesigner(state, "selectedNodeInfo", action.payload);
      })
      .addCase(setSelectedEdgeInfoToState, (state, action) => {
        writeDesigner(state, "selectedEdgeInfo", action.payload);
      })
      .addCase(setOpenRightPanelToState, (state, action) => {
        writeDesigner(state, "openRightPanel", action.payload);
      })
      .addCase(setFirstNodeInfoToState, (state, action) => {
        writeDesigner(state, "firstNodeInfo", action.payload);
      })
      .addCase(setInitialNodeTextToState, (state, action) => {
        // Template placeholder text is not part of the automation draft.
        if (state.isTemplate) return;
        state.initialNodeText = action.payload;
        state.hasUnsavedChanges = true;
      })
      .addCase(setDisabledWFToState, (state, action) => {
        writeDesigner(state, "disabledWF", action.payload);
      })
      .addCase(setIsTemplateToState, (state, action) => {
        state.isTemplate = action.payload;
      });
  },
});

export const {
  saveWorkflowSnapshot,
  clearWorkflowSnapshot,
  restoreWorkflowSnapshot,
  markSnapshotRestored,
  // Wizard state actions
  setWorkflowName,
  setCurrentStep,
  setStepsComplete,
  setStepsData,
  updateStepData,
  setWFType,
  setSelectedTrigger,
  setHideAddNode,
  // Right panel form actions
  setRightPanelFormField,
  resetRightPanelForm,
  // Trigger draft actions
  setTriggerDraft,
  resetTriggerDraft,
  // Snapshot metadata
  setSnapshotMode,
  setSnapshotWorkflowId,
  setTemplateDraftMeta,
  setTemplateDraftName,
  clearTemplateDraft,
} = workflowSnapshotSlice.actions;

// Selectors - existing
export const selectWorkflowSnapshot = (state) =>
  state.workflowSnapshot || initialSnapshotState;
export const selectHasUnsavedChanges = (state) =>
  state.workflowSnapshot?.hasUnsavedChanges || false;
export const selectSnapshotMode = (state) =>
  state.workflowSnapshot?.mode || null;
export const selectSnapshotWorkflowId = (state) =>
  state.workflowSnapshot?.workflowId || null;
export const selectIsRestoring = (state) =>
  state.workflowSnapshot?.isRestoring || false;

// Selectors - wizard state
export const selectWorkflowName = (state) =>
  state.workflowSnapshot?.workflowName || "";
export const selectCurrentStep = (state) =>
  state.workflowSnapshot?.currentStep || 1;
export const selectStepsComplete = (state) =>
  state.workflowSnapshot?.stepsComplete || false;
export const selectStepsData = (state) =>
  state.workflowSnapshot?.stepsData || initialSnapshotState.stepsData;
export const selectWFType = (state) => state.workflowSnapshot?.WFType || "";
export const selectSelectedTrigger = (state) =>
  state.workflowSnapshot?.selectedTrigger || null;
export const selectHideAddNode = (state) =>
  state.workflowSnapshot?.hideAddNode ?? true;

// Selectors - right panel form
export const selectRightPanelForm = (state) => {
  const snap = state.workflowSnapshot;
  if (snap?.isTemplate) {
    return snap.templateDraft?.rightPanelForm || initialRightPanelForm;
  }
  return snap?.rightPanelForm || initialRightPanelForm;
};

export const selectTemplateDraft = (state) =>
  state.workflowSnapshot?.templateDraft || initialTemplateDraft;

// Selectors - trigger draft
export const selectTriggerDraft = (state) =>
  state.workflowSnapshot?.triggerDraft || { categoryId: "", triggerId: "" };

// Selectors - designer state from snapshot (for restoration)
export const selectSnapshotNodeId = (state) =>
  state.workflowSnapshot?.nodeId || "1";
export const selectSnapshotNodes = (state) =>
  state.workflowSnapshot?.nodes || [];
export const selectSnapshotEdges = (state) =>
  state.workflowSnapshot?.edges || [];
export const selectSnapshotSelectedNodeInfo = (state) =>
  state.workflowSnapshot?.selectedNodeInfo || null;
export const selectSnapshotSelectedEdgeInfo = (state) =>
  state.workflowSnapshot?.selectedEdgeInfo || null;
export const selectSnapshotOpenRightPanel = (state) =>
  state.workflowSnapshot?.openRightPanel || false;
export const selectSnapshotFirstNodeInfo = (state) =>
  state.workflowSnapshot?.firstNodeInfo || null;
export const selectSnapshotInitialNodeText = (state) =>
  state.workflowSnapshot?.initialNodeText || "";
export const selectSnapshotDisabledWF = (state) =>
  state.workflowSnapshot?.disabledWF || false;
export const selectSnapshotIsTemplate = (state) =>
  state.workflowSnapshot?.isTemplate || false;

export default workflowSnapshotSlice.reducer;
